import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useFormData, useFormActions, useOcrAssisted, useCoperturaFornitura } from '@hooks/useFormStore'
import { useOperatorAuth } from '@hooks/useOperatorAuth'
import { Button } from '@components/UI/Button'
import { Input } from '@components/UI/Input'
import { SearchableSelect } from '@components/UI/SearchableSelect'
import { CostiVolturaModal } from './CostiVolturaModal'
import { FORNITORI_LUCE, FORNITORI_GAS } from '@/data/fornitori'
import { prodottiApi } from '@services/api'
import { resolveProdottoIdByCompany } from '@/utils/resolveProdottoByCompany'
import { clsx } from 'clsx'
import type { FormData, CategoriaUsoGas, TipoAttivazioneLuce, TipoAttivazioneGas, TipoTitolaritaImmobile, Prodotto } from '@/types'
import styles from './FormSection.module.css'

type FornituraFields = Pick<FormData,
  'tipo_fornitura' | 'ha_luce' | 'ha_gas' |
  'pod_luce' | 'potenza_impegnata' | 'consumo_annuo_luce' | 'fornitore_attuale_luce' |
  'profilo_ee' | 'convenzione_ee' | 'data_attivazione_ee' |
  'pdr_gas' | 'matricola_contatore' | 'consumo_annuo_gas' | 'fornitore_attuale_gas' |
  'profilo_gas' | 'convenzione_gas' | 'data_attivazione_gas' |
  'tipo_attivazione_luce' | 'tipo_attivazione_gas' |
  'voltura_luce_accettata' | 'voltura_gas_accettata' |
  'voltura_mortis_causa_luce' | 'voltura_mortis_causa_gas' |
  'mortis_causa_data_decesso_luce' | 'mortis_causa_data_decesso_gas' |
  'mortis_causa_residenza_6_mesi_luce' | 'mortis_causa_residenza_6_mesi_gas' |
  'vecchio_intestatario_luce' | 'vecchio_intestatario_gas' |
  'richiesta_esecuzione_immediata' | 'categoria_uso_gas' |
  'diario_consumi_attivo' | 'superficie_abitazione' | 'numero_abitanti' |
  'tipologia_abitazione' | 'proprietario_abitazione' |
  'titolarita_immobile' | 'catasto_sezione' | 'catasto_foglio' |
  'catasto_particella' | 'catasto_subalterno' | 'titolarita_note' |
  'locazione_agenzia_entrate' | 'locazione_data_registrazione' | 'locazione_numero' |
  'prodotto_luce_id' | 'prodotto_gas_id' | 'vas_luce' | 'vas_gas' |
  'codice_ateco' | 'tipo_attivita' | 'iva_aliquota_ee' | 'iva_aliquota_gas' |
  'accisa_usi_industriali' | 'tensione_fornitura' | 'provenienza_mercato' | 'numero_addetti'
>

const CATEGORIE_USO_GAS: { value: CategoriaUsoGas; label: string }[] = [
  { value: 'cottura', label: 'Cottura' },
  { value: 'acqua_calda', label: 'Acqua Calda' },
  { value: 'riscaldamento_autonomo', label: 'Riscaldamento Autonomo' },
  { value: 'riscaldamento_centralizzato', label: 'Riscaldamento Centralizzato' }
]

// Opzioni titolarita immobile (per voltura)
const TITOLARITA_IMMOBILE_OPTIONS: { value: TipoTitolaritaImmobile; label: string }[] = [
  { value: 'proprietario', label: 'PROPRIETARIO' },
  { value: 'locatario', label: 'LOCATARIO' },
  { value: 'locatario_edilizia_pubblica', label: 'LOCATARIO Edilizia Pubblica' },
  { value: 'comodatario', label: 'COMODATARIO' },
  { value: 'usufruttuario', label: 'USUFRUTTUARIO' },
  { value: 'altro', label: 'ALTRO' }
]

// Profili prodotto per commodity x segmento clientela.
// La lettera viene assegnata in base al consumo annuo del cliente; il "nome"
// della famiglia (Small/Medium/Large) e' invece configurato per prodotto
// dal Product Manager e mostrato qui in label.
type ProfiloScalaItem = { value: string; range: string; maxConsumo: number | null }

const PROFILI_LUCE_DOMESTICO: ProfiloScalaItem[] = [
  { value: 'A', range: '0 - 1.000 kWh/anno',     maxConsumo: 1000 },
  { value: 'B', range: '1.001 - 2.000 kWh/anno', maxConsumo: 2000 },
  { value: 'C', range: '2.001 - 3.000 kWh/anno', maxConsumo: 3000 },
  { value: 'D', range: '3.001 - 4.000 kWh/anno', maxConsumo: 4000 },
  { value: 'E', range: '4.001 - 6.000 kWh/anno', maxConsumo: 6000 },
  { value: 'F', range: '> 6.000 kWh/anno',       maxConsumo: null }
]

const PROFILI_LUCE_BUSINESS: ProfiloScalaItem[] = [
  { value: 'A', range: '0 - 1.000 kWh/anno',       maxConsumo: 1000 },
  { value: 'B', range: '1.001 - 2.500 kWh/anno',   maxConsumo: 2500 },
  { value: 'C', range: '2.501 - 4.500 kWh/anno',   maxConsumo: 4500 },
  { value: 'D', range: '4.501 - 6.500 kWh/anno',   maxConsumo: 6500 },
  { value: 'E', range: '6.501 - 8.500 kWh/anno',   maxConsumo: 8500 },
  { value: 'F', range: '8.501 - 10.000 kWh/anno',  maxConsumo: 10000 },
  { value: 'G', range: '> 10.000 kWh/anno',        maxConsumo: null }
]

const PROFILI_GAS_DOMESTICO: ProfiloScalaItem[] = [
  { value: 'A', range: '0 - 500 Smc/anno',       maxConsumo: 500 },
  { value: 'B', range: '501 - 1.000 Smc/anno',   maxConsumo: 1000 },
  { value: 'C', range: '1.001 - 1.400 Smc/anno', maxConsumo: 1400 },
  { value: 'D', range: '1.401 - 2.000 Smc/anno', maxConsumo: 2000 },
  { value: 'E', range: '2.001 - 3.000 Smc/anno', maxConsumo: 3000 },
  { value: 'F', range: '> 3.000 Smc/anno',       maxConsumo: null }
]

const PROFILI_GAS_BUSINESS: ProfiloScalaItem[] = [
  { value: 'A', range: '0 - 500 Smc/anno',       maxConsumo: 500 },
  { value: 'B', range: '501 - 1.000 Smc/anno',   maxConsumo: 1000 },
  { value: 'C', range: '1.001 - 1.400 Smc/anno', maxConsumo: 1400 },
  { value: 'D', range: '1.401 - 2.000 Smc/anno', maxConsumo: 2000 },
  { value: 'E', range: '2.001 - 3.000 Smc/anno', maxConsumo: 3000 },
  { value: 'F', range: '3.001 - 5.000 Smc/anno', maxConsumo: 5000 },
  { value: 'G', range: '> 5.000 Smc/anno',       maxConsumo: null }
]

const getScalaLuce = (isBusiness: boolean): ProfiloScalaItem[] =>
  isBusiness ? PROFILI_LUCE_BUSINESS : PROFILI_LUCE_DOMESTICO

const getScalaGas = (isBusiness: boolean): ProfiloScalaItem[] =>
  isBusiness ? PROFILI_GAS_BUSINESS : PROFILI_GAS_DOMESTICO

const calcolaLetteraProfilo = (consumo: number | null | undefined, scala: ProfiloScalaItem[]): string => {
  if (!consumo || consumo <= 0) return ''
  for (const step of scala) {
    if (step.maxConsumo === null) return step.value
    if (consumo <= step.maxConsumo) return step.value
  }
  return scala[scala.length - 1]?.value ?? ''
}

// Funzione calcolo profilo Luce in base al consumo annuo
const calcolaProfiloLuce = (consumo: number | null, isBusiness: boolean): string =>
  calcolaLetteraProfilo(consumo, getScalaLuce(isBusiness))

// Funzione calcolo profilo Gas in base al consumo annuo
const calcolaProfiloGas = (consumo: number | null, isBusiness: boolean): string =>
  calcolaLetteraProfilo(consumo, getScalaGas(isBusiness))

// Helper per ottenere il range label del profilo
const getProfiloLuceRange = (profilo: string, isBusiness: boolean): string => {
  const found = getScalaLuce(isBusiness).find(p => p.value === profilo)
  return found?.range || ''
}

const getProfiloGasRange = (profilo: string, isBusiness: boolean): string => {
  const found = getScalaGas(isBusiness).find(p => p.value === profilo)
  return found?.range || ''
}

// Tipologie attivazione per LUCE
const TIPOLOGIE_ATTIVAZIONE_LUCE: { value: TipoAttivazioneLuce; label: string; description: string; icon: string; tooltip?: string }[] = [
  {
    value: 'cambio_fornitore',
    label: 'Cambio Fornitore',
    description: 'La luce e a mio nome, cambio solo fornitore',
    icon: 'arrow-repeat',
    tooltip: 'Scegli questa opzione se la bolletta attuale e gia intestata a te e vuoi semplicemente cambiare gestore'
  },
  {
    value: 'cambio_fornitore_voltura',
    label: 'Cambio Fornitore con Voltura',
    description: 'Cambio gestore con cambio di intestatario',
    icon: 'person-lines-fill',
    tooltip: 'Scegli questa opzione se la bolletta e intestata a un\'altra persona (es. precedente inquilino) e vuoi intestarla a te'
  },
  {
    value: 'subentro',
    label: 'Subentro',
    description: 'Riattivazione di un contatore chiuso/disattivato',
    icon: 'plug',
    tooltip: 'Scegli questa opzione se il contatore e stato chiuso e vuoi riattivarlo a tuo nome'
  }
]

// Tipologie attivazione per GAS
const TIPOLOGIE_ATTIVAZIONE_GAS: { value: TipoAttivazioneGas; label: string; description: string; icon: string; tooltip?: string }[] = [
  {
    value: 'cambio_fornitore',
    label: 'Cambio Fornitore',
    description: 'Il gas e a mio nome, cambio solo fornitore',
    icon: 'arrow-repeat',
    tooltip: 'Scegli questa opzione se la bolletta attuale e gia intestata a te e vuoi semplicemente cambiare gestore'
  },
  {
    value: 'cambio_fornitore_voltura',
    label: 'Cambio Fornitore con Voltura',
    description: 'Cambio gestore con cambio di intestatario',
    icon: 'person-lines-fill',
    tooltip: 'Scegli questa opzione se la bolletta e intestata a un\'altra persona (es. precedente inquilino) e vuoi intestarla a te'
  },
  {
    value: 'subentro',
    label: 'Subentro',
    description: 'Riattivazione di un contatore chiuso/disattivato',
    icon: 'plug',
    tooltip: 'Scegli questa opzione se il contatore e stato chiuso e vuoi riattivarlo a tuo nome'
  }
]

const ATTIVITA_SVOLTE: { value: string; label: string }[] = [
  { value: 'agricoltura', label: 'Agricoltura' },
  { value: 'attivita_ricreative_sportive', label: 'Attivita ricreative/sportive' },
  { value: 'negozi_specializzati', label: 'Negozi specializzati' },
  { value: 'credito_assicurazioni', label: 'Credito e assicurazioni' },
  { value: 'bar_ristorazione', label: 'Bar/ristorazione' },
  { value: 'grandi_magazzini', label: 'Grandi magazzini, iper/supermercati' },
  { value: 'lavorazioni_elettriche', label: 'Lavorazioni elettriche' },
  { value: 'lavorazioni_legno', label: 'Lavorazioni del legno' },
  { value: 'industria_alimentare', label: 'Industria alimentare' },
  { value: 'lavorazioni_gomma_plastica', label: 'Lavorazioni gomma/plastica' },
  { value: 'lavorazioni_meccaniche', label: 'Lavorazioni meccaniche' },
  { value: 'lavorazioni_chimiche', label: 'Lavorazioni chimiche' },
  { value: 'industria_metallurgica', label: 'Industria metallurgica' },
  { value: 'lavorazioni_ceramiche', label: 'Lavorazioni ceramiche' },
  { value: 'lavorazioni_tessili', label: 'Lavorazioni tessili/calzaturifici' },
  { value: 'edilizia', label: 'Edilizia' },
  { value: 'servizi_imprese', label: 'Servizi alle imprese' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'ospedali_case_cura', label: 'Ospedali/case di cura' },
  { value: 'pubblica_amministrazione', label: 'Pubblica Amministrazione' }
]

export function FornituraSection() {
  const formData = useFormData()
  const { setData, nextStep, prevStep } = useFormActions()
  const ocrAssisted = useOcrAssisted()
  const coperturaFornitura = useCoperturaFornitura()
  // VAS "Hera NoProblem": add-on visibile solo agli operatori abilitati (Adventure).
  const { operator } = useOperatorAuth()
  const vasEnabled = !!operator?.vas_enabled
  
  // Verifica disponibilita luce/gas dal controllo copertura
  const luceDisponibile = coperturaFornitura?.luce_disponibile ?? true
  const gasDisponibile = coperturaFornitura?.gas_disponibile ?? true
  
  // Modal state per voltura (luce e gas)
  const [showCostiVolturaModal, setShowCostiVolturaModal] = useState(false)
  const [pendingVolturaType, setPendingVolturaType] = useState<'luce' | 'gas' | null>(null)
  
  // Categoria d'uso gas state
  const [categoriaUsoGas, setCategoriaUsoGas] = useState<CategoriaUsoGas[]>(formData.categoria_uso_gas || [])

  // Prodotti da API: filtrati per segmento (domestico vs business) per non mischiare le CTE.
  const [prodottiLuce, setProdottiLuce] = useState<Prodotto[]>([])
  const [prodottiGas, setProdottiGas] = useState<Prodotto[]>([])

  const tipoClienteForCatalog: 'domestico' | 'business' =
    formData.tipo_cliente === 'business' ? 'business' : 'domestico'

  useEffect(() => {
    prodottiApi
      .getProdotti({ commodity: 'luce', attivo: true, tipo_cliente: tipoClienteForCatalog })
      .then(setProdottiLuce)
      .catch(() => setProdottiLuce([]))
    prodottiApi
      .getProdotti({ commodity: 'gas', attivo: true, tipo_cliente: tipoClienteForCatalog })
      .then(setProdottiGas)
      .catch(() => setProdottiGas([]))
  }, [tipoClienteForCatalog])

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FornituraFields>({
    mode: 'onBlur',
    defaultValues: {
      tipo_fornitura: formData.tipo_fornitura,
      ha_luce: formData.ha_luce,
      ha_gas: formData.ha_gas,
      pod_luce: formData.pod_luce,
      potenza_impegnata: formData.potenza_impegnata,
      consumo_annuo_luce: formData.consumo_annuo_luce,
      profilo_ee: formData.profilo_ee,
      convenzione_ee: formData.convenzione_ee,
      data_attivazione_ee: formData.data_attivazione_ee,
      fornitore_attuale_luce: formData.fornitore_attuale_luce,
      pdr_gas: formData.pdr_gas,
      matricola_contatore: formData.matricola_contatore,
      consumo_annuo_gas: formData.consumo_annuo_gas,
      profilo_gas: formData.profilo_gas,
      convenzione_gas: formData.convenzione_gas,
      data_attivazione_gas: formData.data_attivazione_gas,
      fornitore_attuale_gas: formData.fornitore_attuale_gas,
      tipo_attivazione_luce: formData.tipo_attivazione_luce,
      tipo_attivazione_gas: formData.tipo_attivazione_gas,
      voltura_luce_accettata: formData.voltura_luce_accettata,
      voltura_gas_accettata: formData.voltura_gas_accettata,
      voltura_mortis_causa_luce: formData.voltura_mortis_causa_luce,
      voltura_mortis_causa_gas: formData.voltura_mortis_causa_gas,
      mortis_causa_data_decesso_luce: formData.mortis_causa_data_decesso_luce,
      mortis_causa_data_decesso_gas: formData.mortis_causa_data_decesso_gas,
      mortis_causa_residenza_6_mesi_luce: formData.mortis_causa_residenza_6_mesi_luce,
      mortis_causa_residenza_6_mesi_gas: formData.mortis_causa_residenza_6_mesi_gas,
      vecchio_intestatario_luce: formData.vecchio_intestatario_luce,
      vecchio_intestatario_gas: formData.vecchio_intestatario_gas,
      richiesta_esecuzione_immediata: formData.richiesta_esecuzione_immediata,
      categoria_uso_gas: formData.categoria_uso_gas || [],
      diario_consumi_attivo: formData.diario_consumi_attivo,
      superficie_abitazione: formData.superficie_abitazione,
      numero_abitanti: formData.numero_abitanti,
      tipologia_abitazione: formData.tipologia_abitazione,
      proprietario_abitazione: formData.proprietario_abitazione,
      // Titolarita Immobile (voltura)
      titolarita_immobile: formData.titolarita_immobile,
      catasto_sezione: formData.catasto_sezione,
      catasto_foglio: formData.catasto_foglio,
      catasto_particella: formData.catasto_particella,
      catasto_subalterno: formData.catasto_subalterno,
      titolarita_note: formData.titolarita_note,
      // Dati Locazione (per locatario/comodatario)
      locazione_agenzia_entrate: formData.locazione_agenzia_entrate,
      locazione_data_registrazione: formData.locazione_data_registrazione,
      locazione_numero: formData.locazione_numero,
      // Prodotti
      prodotto_luce_id: formData.prodotto_luce_id,
      prodotto_gas_id: formData.prodotto_gas_id,
      vas_luce: formData.vas_luce,
      vas_gas: formData.vas_gas,
      // Business - Scheda Tecnica
      codice_ateco: formData.codice_ateco,
      tipo_attivita: formData.tipo_attivita,
      iva_aliquota_ee: formData.iva_aliquota_ee,
      iva_aliquota_gas: formData.iva_aliquota_gas,
      accisa_usi_industriali: formData.accisa_usi_industriali,
      tensione_fornitura: formData.tensione_fornitura,
      provenienza_mercato: formData.provenienza_mercato,
      numero_addetti: formData.numero_addetti
    }
  })
  
  // Toggle categoria uso gas
  const toggleCategoriaUsoGas = (categoria: CategoriaUsoGas) => {
    setCategoriaUsoGas(prev => {
      const newValue = prev.includes(categoria)
        ? prev.filter(c => c !== categoria)
        : [...prev, categoria]
      setValue('categoria_uso_gas', newValue)
      return newValue
    })
  }

  // Auto-seleziona tipo fornitura basandosi sulla copertura disponibile
  useEffect(() => {
    // Se solo luce disponibile, forza selezione luce
    if (luceDisponibile && !gasDisponibile) {
      setValue('tipo_fornitura', 'luce')
      setValue('ha_luce', true)
      setValue('ha_gas', false)
    }
    // Se solo gas disponibile, forza selezione gas
    else if (!luceDisponibile && gasDisponibile) {
      setValue('tipo_fornitura', 'gas')
      setValue('ha_luce', false)
      setValue('ha_gas', true)
    }
    // Se entrambi disponibili ma selezione attuale non valida, reset
    else if (luceDisponibile && gasDisponibile) {
      // Mantieni la selezione corrente
    }
  }, [luceDisponibile, gasDisponibile, setValue])

  const tipoFornitura = watch('tipo_fornitura')
  const tipoAttivazioneLuce = watch('tipo_attivazione_luce')
  const tipoAttivazioneGas = watch('tipo_attivazione_gas')
  const volturaLuceAccettata = watch('voltura_luce_accettata')
  const volturaGasAccettata = watch('voltura_gas_accettata')
  const vecchioIntestatarioLuce = watch('vecchio_intestatario_luce')
  const vecchioIntestatarioGas = watch('vecchio_intestatario_gas')
  const mortisCausaLuce = watch('voltura_mortis_causa_luce')
  const mortisCausaGas = watch('voltura_mortis_causa_gas')
  const consumoAnnuoLuce = watch('consumo_annuo_luce')
  const consumoAnnuoGas = watch('consumo_annuo_gas')
  const profiloEE = watch('profilo_ee')
  const profiloGasValue = watch('profilo_gas')
  const prodottoLuceId = watch('prodotto_luce_id')
  const prodottoGasId = watch('prodotto_gas_id')
  const fornitoreAttualeLuceValue = watch('fornitore_attuale_luce')
  const fornitoreAttualeGasValue = watch('fornitore_attuale_gas')
  const diarioConsumiAttivo = watch('diario_consumi_attivo')
  
  const hasLuce = tipoFornitura === 'luce' || tipoFornitura === 'entrambi'
  const hasGas = tipoFornitura === 'gas' || tipoFornitura === 'entrambi'
  const isBusiness = formData.tipo_cliente === 'business'

  // Famiglia profilo (Small/Medium/Large) del prodotto selezionato per ciascuna
  // commodity. Fallback "Large" per retro-compatibilita' con prodotti vecchi
  // privi del campo profilo_famiglia.
  const famigliaLuce: string =
    (prodottiLuce.find(p => p.id === prodottoLuceId)?.profilo_famiglia) || 'Large'
  const famigliaGas: string =
    (prodottiGas.find(p => p.id === prodottoGasId)?.profilo_famiglia) || 'Large'

  /** Priorità allo store copertura (stessa verifica API dell’anagrafica), poi formData. */
  const companyForProdotti =
    coperturaFornitura?.company ?? formData.company ?? null

  useEffect(() => {
    if (!hasLuce || prodottiLuce.length === 0) return
    const id = resolveProdottoIdByCompany(prodottiLuce, companyForProdotti, 'luce')
    if (id != null && id !== formData.prodotto_luce_id) {
      setValue('prodotto_luce_id', id)
      setData({ prodotto_luce_id: id })
    }
  }, [
    hasLuce,
    prodottiLuce,
    companyForProdotti,
    formData.prodotto_luce_id,
    setValue,
    setData,
  ])

  useEffect(() => {
    if (!hasGas || prodottiGas.length === 0) return
    const id = resolveProdottoIdByCompany(prodottiGas, companyForProdotti, 'gas')
    if (id != null && id !== formData.prodotto_gas_id) {
      setValue('prodotto_gas_id', id)
      setData({ prodotto_gas_id: id })
    }
  }, [
    hasGas,
    prodottiGas,
    companyForProdotti,
    formData.prodotto_gas_id,
    setValue,
    setData,
  ])
  
  // Auto-calcola profilo luce quando cambia il consumo annuo o il segmento
  // di clientela (dom/business hanno scaglioni diversi).
  useEffect(() => {
    const consumo = consumoAnnuoLuce || formData.consumo_annuo_luce
    if (consumo && consumo > 0) {
      const nuovoProfilo = calcolaProfiloLuce(consumo, isBusiness)
      if (nuovoProfilo && nuovoProfilo !== profiloEE) {
        setValue('profilo_ee', nuovoProfilo)
      }
    }
  }, [consumoAnnuoLuce, formData.consumo_annuo_luce, isBusiness, setValue, profiloEE])

  // Auto-calcola profilo gas quando cambia il consumo annuo o il segmento.
  useEffect(() => {
    const consumo = consumoAnnuoGas || formData.consumo_annuo_gas
    if (consumo && consumo > 0) {
      const nuovoProfilo = calcolaProfiloGas(consumo, isBusiness)
      if (nuovoProfilo && nuovoProfilo !== profiloGasValue) {
        setValue('profilo_gas', nuovoProfilo)
      }
    }
  }, [consumoAnnuoGas, formData.consumo_annuo_gas, isBusiness, setValue, profiloGasValue])
  
  // Diario consumi: proprietario sempre attivo
  useEffect(() => {
    if (diarioConsumiAttivo) {
      setValue('proprietario_abitazione', true)
    }
  }, [diarioConsumiAttivo, setValue])

  // Voltura disponibile per LUCE e GAS
  const needsVolturaLuce = hasLuce && tipoAttivazioneLuce === 'cambio_fornitore_voltura'
  const needsVolturaGas = hasGas && tipoAttivazioneGas === 'cambio_fornitore_voltura'

  // Subentro per LUCE e GAS
  const needsSubentroLuce = hasLuce && tipoAttivazioneLuce === 'subentro'
  const needsSubentroGas = hasGas && tipoAttivazioneGas === 'subentro'

  // Dichiarazione sostitutiva necessaria per voltura E subentro
  const needsDichiarazione = needsVolturaLuce || needsVolturaGas || needsSubentroLuce || needsSubentroGas

  // Richiesta esecuzione immediata del servizio durante il periodo di
  // recesso: per il BUSINESS in caso di voltura o subentro deve essere
  // sempre pre-flaggata e non removibile (richiesta contrattuale Hera per
  // attivare il servizio senza attendere i 14 giorni). Per il domestico
  // resta opzionale e modificabile dall'operatore.
  const richiestaEsecuzioneImmediataLocked = isBusiness && needsDichiarazione
  useEffect(() => {
    if (richiestaEsecuzioneImmediataLocked) {
      setValue('richiesta_esecuzione_immediata', true, { shouldDirty: false })
    }
  }, [richiestaEsecuzioneImmediataLocked, setValue])

  // Mostra campo fornitore attuale per cambio fornitore (sempre richiesto)
  const needsFornitoreAttualeLuce = hasLuce && (tipoAttivazioneLuce === 'cambio_fornitore' || tipoAttivazioneLuce === 'cambio_fornitore_voltura')
  const needsFornitoreAttualeGas = hasGas && (tipoAttivazioneGas === 'cambio_fornitore' || tipoAttivazioneGas === 'cambio_fornitore_voltura')

  const handleTipoChange = (tipo: 'luce' | 'gas' | 'entrambi') => {
    setValue('tipo_fornitura', tipo)
    setValue('ha_luce', tipo === 'luce' || tipo === 'entrambi')
    setValue('ha_gas', tipo === 'gas' || tipo === 'entrambi')
    
    // Reset tipologia attivazione when changing tipo fornitura
    if (tipo === 'luce') {
      setValue('tipo_attivazione_gas', null)
      setValue('voltura_gas_accettata', false)
      setValue('vecchio_intestatario_gas', null)
    } else if (tipo === 'gas') {
      setValue('tipo_attivazione_luce', null)
      setValue('voltura_luce_accettata', false)
      setValue('vecchio_intestatario_luce', null)
    }
  }

  // Handler per cambio tipo attivazione LUCE
  const handleTipoAttivazioneLuceChange = (tipoAttivazione: TipoAttivazioneLuce | null) => {
    setValue('tipo_attivazione_luce', tipoAttivazione)
    
    // Reset voltura state se non e voltura
    if (tipoAttivazione !== 'cambio_fornitore_voltura') {
      setValue('voltura_luce_accettata', false)
      setValue('vecchio_intestatario_luce', null)
    } else if (!vecchioIntestatarioLuce) {
      // Pre-popola con dati OCR se disponibili (per voltura i dati bolletta sono del vecchio intestatario)
      const ocrDataForVoltura = ocrAssisted ? {
        nome: formData.nome || '',
        cognome: formData.cognome || '',
        codice_fiscale: formData.codice_fiscale || ''
      } : { nome: '', cognome: '', codice_fiscale: '' }
      setValue('vecchio_intestatario_luce', ocrDataForVoltura)
    }
    
    // Show modal for voltura luce
    if (tipoAttivazione === 'cambio_fornitore_voltura' && !volturaLuceAccettata) {
      setPendingVolturaType('luce')
      setShowCostiVolturaModal(true)
    }
  }

  // Handler per cambio tipo attivazione GAS
  const handleTipoAttivazioneGasChange = (tipoAttivazione: TipoAttivazioneGas | null) => {
    setValue('tipo_attivazione_gas', tipoAttivazione)
    
    // Reset voltura state se non e voltura
    if (tipoAttivazione !== 'cambio_fornitore_voltura') {
      setValue('voltura_gas_accettata', false)
      setValue('vecchio_intestatario_gas', null)
    } else if (!vecchioIntestatarioGas) {
      // Pre-popola con dati OCR se disponibili
      const ocrDataForVoltura = ocrAssisted ? {
        nome: formData.nome || '',
        cognome: formData.cognome || '',
        codice_fiscale: formData.codice_fiscale || ''
      } : { nome: '', cognome: '', codice_fiscale: '' }
      setValue('vecchio_intestatario_gas', ocrDataForVoltura)
    }
    
    // Show modal for voltura gas
    if (tipoAttivazione === 'cambio_fornitore_voltura' && !volturaGasAccettata) {
      setPendingVolturaType('gas')
      setShowCostiVolturaModal(true)
    }
  }

  // Voltura modal handlers (per LUCE e GAS)
  const handleVolturaAccepted = () => {
    if (pendingVolturaType === 'luce') {
      setValue('voltura_luce_accettata', true)
    } else if (pendingVolturaType === 'gas') {
      setValue('voltura_gas_accettata', true)
    }
    setShowCostiVolturaModal(false)
    setPendingVolturaType(null)
  }

  const handleVolturaClose = () => {
    if (pendingVolturaType === 'luce') {
      setValue('tipo_attivazione_luce', null)
      setValue('vecchio_intestatario_luce', null)
    } else if (pendingVolturaType === 'gas') {
      setValue('tipo_attivazione_gas', null)
      setValue('vecchio_intestatario_gas', null)
    }
    setShowCostiVolturaModal(false)
    setPendingVolturaType(null)
  }

  // State per errore tipo attivazione mancante
  const [missingAttivazione, setMissingAttivazione] = useState<'luce' | 'gas' | 'entrambi' | null>(null)

  const onSubmit = (data: FornituraFields) => {
    // Reset errore
    setMissingAttivazione(null)
    
    // VALIDAZIONE: Tipo attivazione obbligatorio
    const needsLuceAttivazione = hasLuce && !data.tipo_attivazione_luce
    const needsGasAttivazione = hasGas && !data.tipo_attivazione_gas
    
    if (needsLuceAttivazione && needsGasAttivazione) {
      setMissingAttivazione('entrambi')
      return
    } else if (needsLuceAttivazione) {
      setMissingAttivazione('luce')
      return
    } else if (needsGasAttivazione) {
      setMissingAttivazione('gas')
      return
    }
    
    // Validate voltura acceptance per LUCE
    if (needsVolturaLuce && !data.voltura_luce_accettata) {
      setPendingVolturaType('luce')
      setShowCostiVolturaModal(true)
      return
    }
    
    // Validate voltura acceptance per GAS
    if (needsVolturaGas && !data.voltura_gas_accettata) {
      setPendingVolturaType('gas')
      setShowCostiVolturaModal(true)
      return
    }
    
    setData(data)
    nextStep()
  }

  // Render tipologia attivazione selector per LUCE
  const renderTipologiaAttivazioneLuce = () => {
    const hasError = missingAttivazione === 'luce' || missingAttivazione === 'entrambi'
    
    return (
      <div className={clsx(styles.activationSection, hasError && styles.activationError)}>
        <div className={styles.activationTitle}>
          <i className="bi bi-signpost-2" />
          <span>Tipologia di Attivazione <span className={styles.required}>*</span></span>
        </div>
        {hasError && (
          <div className={styles.activationErrorMessage}>
            <i className="bi bi-exclamation-circle" />
            <span>Seleziona una tipologia di attivazione per la luce</span>
          </div>
        )}
        <div className={styles.activationSelector}>
          {TIPOLOGIE_ATTIVAZIONE_LUCE.map((tipo) => (
            <button
              key={tipo.value}
              type="button"
              className={clsx(styles.activationButton, {
                [styles.activationActive]: tipoAttivazioneLuce === tipo.value,
                [styles.activationAccepted]: tipo.value === 'cambio_fornitore_voltura' && tipoAttivazioneLuce === tipo.value && volturaLuceAccettata
              })}
              onClick={() => handleTipoAttivazioneLuceChange(tipo.value)}
              title={tipo.tooltip}
            >
              <div className={styles.activationIcon}>
                <i className={`bi bi-${tipo.icon}`} />
              </div>
              <div className={styles.activationContent}>
                <span className={styles.activationLabel}>{tipo.label}</span>
                <span className={styles.activationDesc}>{tipo.description}</span>
              </div>
              {tipoAttivazioneLuce === tipo.value && (
                <div className={styles.activationCheck}>
                  <i className="bi bi-check-lg" />
                </div>
              )}
            </button>
          ))}
        </div>
        
        {/* Badge voltura accettata */}
        {tipoAttivazioneLuce === 'cambio_fornitore_voltura' && volturaLuceAccettata && (
          <div className={styles.subentroAcceptedBadge}>
            <i className="bi bi-check-circle-fill" />
            <span>Costi di voltura visualizzati e accettati</span>
          </div>
        )}
      </div>
    )
  }

  // Render tipologia attivazione selector per GAS
  const renderTipologiaAttivazioneGas = () => {
    const hasError = missingAttivazione === 'gas' || missingAttivazione === 'entrambi'
    
    return (
      <div className={clsx(styles.activationSection, hasError && styles.activationError)}>
        <div className={styles.activationTitle}>
          <i className="bi bi-signpost-2" />
          <span>Tipologia di Attivazione <span className={styles.required}>*</span></span>
        </div>
        {hasError && (
          <div className={styles.activationErrorMessage}>
            <i className="bi bi-exclamation-circle" />
            <span>Seleziona una tipologia di attivazione per il gas</span>
          </div>
        )}
        <div className={styles.activationSelector}>
          {TIPOLOGIE_ATTIVAZIONE_GAS.map((tipo) => (
            <button
              key={tipo.value}
              type="button"
              className={clsx(styles.activationButton, {
                [styles.activationActive]: tipoAttivazioneGas === tipo.value,
                [styles.activationAccepted]: tipo.value === 'cambio_fornitore_voltura' && tipoAttivazioneGas === tipo.value && volturaGasAccettata
              })}
              onClick={() => handleTipoAttivazioneGasChange(tipo.value)}
              title={tipo.tooltip}
            >
              <div className={styles.activationIcon}>
                <i className={`bi bi-${tipo.icon}`} />
              </div>
              <div className={styles.activationContent}>
                <span className={styles.activationLabel}>{tipo.label}</span>
                <span className={styles.activationDesc}>{tipo.description}</span>
              </div>
              {tipoAttivazioneGas === tipo.value && (
                <div className={styles.activationCheck}>
                  <i className="bi bi-check-lg" />
                </div>
              )}
            </button>
          ))}
        </div>
        
        {/* Badge voltura accettata */}
        {tipoAttivazioneGas === 'cambio_fornitore_voltura' && volturaGasAccettata && (
          <div className={styles.subentroAcceptedBadge}>
            <i className="bi bi-check-circle-fill" />
            <span>Costi di voltura visualizzati e accettati</span>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionIcon}>
            <i className="bi bi-lightning-charge-fill" />
          </div>
          <div>
            <h2 className={styles.sectionTitle}>Dati Fornitura</h2>
            <p className={styles.sectionDescription}>
              Seleziona il tipo di fornitura e inserisci i dati
            </p>
          </div>
        </div>

        {/* Messaggio copertura parziale */}
        {coperturaFornitura && coperturaFornitura.verificato && (!luceDisponibile || !gasDisponibile) && (
          <div className={styles.coperturaWarning}>
            <div className={styles.coperturaWarningHeader}>
              <i className="bi bi-geo-alt-fill" />
              <span className={styles.coperturaWarningTitle}>
                Copertura parziale {coperturaFornitura.comune ? `\u2013 ${coperturaFornitura.comune}` : ''}
              </span>
            </div>
            <div className={styles.coperturaWarningServices}>
              <div className={`${styles.coperturaWarningService} ${luceDisponibile ? styles.coperturaServiceOk : styles.coperturaServiceNo}`}>
                <i className={`bi ${luceDisponibile ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`} />
                <span>Luce</span>
              </div>
              <div className={`${styles.coperturaWarningService} ${gasDisponibile ? styles.coperturaServiceOk : styles.coperturaServiceNo}`}>
                <i className={`bi ${gasDisponibile ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`} />
                <span>Gas</span>
              </div>
            </div>
          </div>
        )}

        {/* Tipo fornitura selector */}
        <div className={styles.typeSelector}>
          <button
            type="button"
            className={clsx(styles.typeButton, {
              [styles.typeActive]: tipoFornitura === 'luce',
              [styles.typeDisabled]: !luceDisponibile
            })}
            onClick={() => luceDisponibile && handleTipoChange('luce')}
            disabled={!luceDisponibile}
          >
            <i className="bi bi-lightbulb" />
            <span>Solo Luce</span>
            {!luceDisponibile && <small className={styles.nonDisponibile}>Non disponibile</small>}
          </button>
          
          <button
            type="button"
            className={clsx(styles.typeButton, {
              [styles.typeActive]: tipoFornitura === 'gas',
              [styles.typeDisabled]: !gasDisponibile
            })}
            onClick={() => gasDisponibile && handleTipoChange('gas')}
            disabled={!gasDisponibile}
          >
            <i className="bi bi-fire" />
            <span>Solo Gas</span>
            {!gasDisponibile && <small className={styles.nonDisponibile}>Non disponibile</small>}
          </button>
          
          <button
            type="button"
            className={clsx(styles.typeButton, {
              [styles.typeActive]: tipoFornitura === 'entrambi',
              [styles.typeDisabled]: !luceDisponibile || !gasDisponibile
            })}
            onClick={() => luceDisponibile && gasDisponibile && handleTipoChange('entrambi')}
            disabled={!luceDisponibile || !gasDisponibile}
          >
            <i className="bi bi-house" />
            <span>Luce + Gas</span>
            {(!luceDisponibile || !gasDisponibile) && <small className={styles.nonDisponibile}>Non disponibile</small>}
          </button>
        </div>

        {/* Dati Luce */}
        {hasLuce && (
          <div className={styles.supplySection}>
            <div className={styles.supplySectionHeader}>
              <i className="bi bi-lightbulb-fill" />
              <span>Fornitura Luce</span>
            </div>
            
            {/* Tipologia Attivazione Luce */}
            {renderTipologiaAttivazioneLuce()}
            
            {/* Fornitore Attuale per Cambio Fornitore */}
            {needsFornitoreAttualeLuce && (
              <div className={styles.fornitoreAttualeSection}>
                <SearchableSelect
                  label="Attuale Fornitore Luce"
                  icon="building"
                  required
                  placeholder="Inizia a digitare per cercare..."
                  hint="Fornitore attuale obbligatorio"
                  options={FORNITORI_LUCE}
                  error={errors.fornitore_attuale_luce?.message}
                  value={fornitoreAttualeLuceValue || ''}
                  {...register('fornitore_attuale_luce', {
                    required: needsFornitoreAttualeLuce ? 'Fornitore attuale obbligatorio' : false
                  })}
                />
              </div>
            )}
            
            {/* Per Voltura LUCE: dati precedente intestatario e checkbox Mortis Causa */}
            {needsVolturaLuce && (
              <div className={styles.vecchioIntestatarioSection}>
                <div className={styles.vecchioIntestatarioHeader}>
                  <i className="bi bi-info-circle" />
                  <span>Opzioni Voltura Luce</span>
                </div>
                
                {/* Dati Precedente Intestatario Luce */}
                {vecchioIntestatarioLuce && (
                  <div className={styles.vecchioIntestatarioDetails}>
                    <div className={styles.vecchioIntestatarioSubHeader}>
                      <i className="bi bi-person-badge" />
                      <span>Dati Precedente Intestatario</span>
                    </div>
                    <div className={styles.formGrid}>
                      <Input
                        name="vecchio_intestatario_luce_nome"
                        label="Nome"
                        icon="person"
                        placeholder="Nome precedente intestatario"
                        value={vecchioIntestatarioLuce.nome || ''}
                        onChange={(e) => setValue('vecchio_intestatario_luce', {
                          ...vecchioIntestatarioLuce,
                          nome: e.target.value
                        })}
                      />
                      <Input
                        name="vecchio_intestatario_luce_cognome"
                        label="Cognome"
                        icon="person"
                        placeholder="Cognome precedente intestatario"
                        value={vecchioIntestatarioLuce.cognome || ''}
                        onChange={(e) => setValue('vecchio_intestatario_luce', {
                          ...vecchioIntestatarioLuce,
                          cognome: e.target.value
                        })}
                      />
                      <Input
                        name="vecchio_intestatario_luce_cf"
                        label="Codice Fiscale"
                        icon="card-text"
                        uppercase
                        placeholder="Codice fiscale precedente intestatario"
                        value={vecchioIntestatarioLuce.codice_fiscale || ''}
                        onChange={(e) => setValue('vecchio_intestatario_luce', {
                          ...vecchioIntestatarioLuce,
                          codice_fiscale: e.target.value.toUpperCase()
                        })}
                      />
                    </div>
                  </div>
                )}
                
                {/* Voltura Mortis Causa: applicabile solo a utenze domestiche di residenza.
                    Per i clienti business l'intero blocco e' nascosto e i relativi campi
                    (voltura_mortis_causa_luce, data decesso, residenza 6 mesi) non vengono
                    raccolti. */}
                {!isBusiness && (
                  <>
                    <div className={styles.checkboxContainer}>
                      <label className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          {...register('voltura_mortis_causa_luce')}
                        />
                        <span className={styles.checkboxCustom}>
                          <i className="bi bi-check-lg" />
                        </span>
                        <span className={styles.checkboxText}>
                          <strong>Voltura mortis causa su utenza domestica di residenza</strong>
                          <small>
                            Da selezionare se si e titolari di un diritto di successione relativo all'immobile oggetto della fornitura
                          </small>
                        </span>
                      </label>
                    </div>

                    {mortisCausaLuce && (
                      <div className={styles.mortisCausaDetails}>
                        <div className={styles.mortisCausaHeader}>
                          <i className="bi bi-calendar-event" />
                          <span>Dati per Voltura Mortis Causa</span>
                        </div>

                        <div className={styles.formGrid}>
                          <Input
                            label="Data Decesso"
                            icon="calendar-event"
                            type="date"
                            required
                            error={errors.mortis_causa_data_decesso_luce?.message}
                            {...register('mortis_causa_data_decesso_luce', {
                              required: mortisCausaLuce ? 'Data decesso obbligatoria per mortis causa' : false,
                              validate: (value) => {
                                if (!mortisCausaLuce || !value) return true
                                const date = new Date(value)
                                const today = new Date()
                                today.setHours(0, 0, 0, 0)
                                if (date > today) return 'La data di decesso non può essere nel futuro'
                                return true
                              }
                            })}
                          />
                        </div>

                        {formData.stesso_indirizzo && (
                          <div className={styles.checkboxContainer} style={{ marginTop: '1rem' }}>
                            <label className={styles.checkboxLabel}>
                              <input
                                type="checkbox"
                                className={styles.checkbox}
                                {...register('mortis_causa_residenza_6_mesi_luce')}
                              />
                              <span className={styles.checkboxCustom}>
                                <i className="bi bi-check-lg" />
                              </span>
                              <span className={styles.checkboxText}>
                                <strong>Dichiarazione di residenza</strong>
                                <small>
                                  Dichiaro di risiedere nell'indirizzo di fornitura da almeno 6 mesi
                                </small>
                              </span>
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            
            <div className={styles.divider}>
              <span>Dati Contatore</span>
            </div>
            
            <div className={styles.formGrid}>
              <div className={styles.fullWidth}>
                <Input
                  label="POD (Codice Punto di Prelievo)"
                  icon="upc"
                  required
                  uppercase
                  maxLength={14}
                  placeholder="IT001E12345678"
                  hint="Trovi il POD nella bolletta della luce (14 caratteri)"
                  error={errors.pod_luce?.message}
                  {...register('pod_luce', {
                    required: hasLuce ? 'POD obbligatorio' : false,
                    pattern: {
                      value: /^[A-Z0-9]{14}$/,
                      message: 'POD deve essere di 14 caratteri (lettere maiuscole e cifre)'
                    }
                  })}
                />
              </div>
              
              <Input
                label="Potenza Impegnata (kW)"
                icon="speedometer2"
                type="number"
                placeholder="3.0"
                error={errors.potenza_impegnata?.message}
                {...register('potenza_impegnata', {
                  valueAsNumber: true
                })}
              />
              
              <Input
                label="Consumo Annuo (kWh)"
                icon="graph-up"
                type="number"
                placeholder="2700"
                required
                error={errors.consumo_annuo_luce?.message}
                {...register('consumo_annuo_luce', {
                  valueAsNumber: true,
                  required: hasLuce ? 'Consumo annuo obbligatorio' : false
                })}
              />
            </div>
            
            <div className={styles.divider}>
              <span>Dati Offerta</span>
            </div>
            
            <div className={styles.formGrid}>
              {/* Profilo calcolato automaticamente dal consumo annuo */}
              <div className={styles.profiloCalcolatoInline}>
                <label>Profilo <span className={styles.required}>*</span></label>
                {profiloEE ? (
                  <div className={styles.profiloBadgeInline}>
                    <i className="bi bi-lightning-charge" />
                    Profilo {famigliaLuce} {profiloEE}
                    <span className={styles.profiloRangeSmall}>({getProfiloLuceRange(profiloEE, isBusiness)})</span>
                  </div>
                ) : (
                  <div className={styles.profiloPlaceholderInline}>
                    <i className="bi bi-info-circle" />
                    Inserisci consumo annuo
                  </div>
                )}
                <input type="hidden" {...register('profilo_ee')} />
              </div>
              
              <Input
                label="Convenzione/Promo"
                icon="tag"
                placeholder="Codice convenzione"
                hint="Codice convenzione o promo (opzionale)"
                error={errors.convenzione_ee?.message}
                {...register('convenzione_ee')}
              />
            </div>
          </div>
        )}

        {/* Dati Gas */}
        {hasGas && (
          <div className={styles.supplySection}>
            <div className={styles.supplySectionHeader}>
              <i className="bi bi-fire" />
              <span>Fornitura Gas</span>
            </div>
            
            {/* Tipologia Attivazione Gas - solo cambio fornitore */}
            {renderTipologiaAttivazioneGas()}
            
            {/* Fornitore Attuale per Cambio Fornitore */}
            {needsFornitoreAttualeGas && (
              <div className={styles.fornitoreAttualeSection}>
                <SearchableSelect
                  label="Attuale Fornitore Gas"
                  icon="building"
                  required
                  placeholder="Inizia a digitare per cercare..."
                  hint="Fornitore attuale obbligatorio"
                  options={FORNITORI_GAS}
                  error={errors.fornitore_attuale_gas?.message}
                  value={fornitoreAttualeGasValue || ''}
                  {...register('fornitore_attuale_gas', {
                    required: needsFornitoreAttualeGas ? 'Fornitore attuale obbligatorio' : false
                  })}
                />
              </div>
            )}
            
            {/* Per Voltura GAS: dati precedente intestatario e checkbox Mortis Causa */}
            {needsVolturaGas && (
              <div className={styles.vecchioIntestatarioSection}>
                <div className={styles.vecchioIntestatarioHeader}>
                  <i className="bi bi-info-circle" />
                  <span>Opzioni Voltura Gas</span>
                </div>
                
                {/* Dati Precedente Intestatario Gas */}
                {vecchioIntestatarioGas && (
                  <div className={styles.vecchioIntestatarioDetails}>
                    <div className={styles.vecchioIntestatarioSubHeader}>
                      <i className="bi bi-person-badge" />
                      <span>Dati Precedente Intestatario</span>
                    </div>
                    <div className={styles.formGrid}>
                      <Input
                        name="vecchio_intestatario_gas_nome"
                        label="Nome"
                        icon="person"
                        placeholder="Nome precedente intestatario"
                        value={vecchioIntestatarioGas.nome || ''}
                        onChange={(e) => setValue('vecchio_intestatario_gas', {
                          ...vecchioIntestatarioGas,
                          nome: e.target.value
                        })}
                      />
                      <Input
                        name="vecchio_intestatario_gas_cognome"
                        label="Cognome"
                        icon="person"
                        placeholder="Cognome precedente intestatario"
                        value={vecchioIntestatarioGas.cognome || ''}
                        onChange={(e) => setValue('vecchio_intestatario_gas', {
                          ...vecchioIntestatarioGas,
                          cognome: e.target.value
                        })}
                      />
                      <Input
                        name="vecchio_intestatario_gas_cf"
                        label="Codice Fiscale"
                        icon="card-text"
                        uppercase
                        placeholder="Codice fiscale precedente intestatario"
                        value={vecchioIntestatarioGas.codice_fiscale || ''}
                        onChange={(e) => setValue('vecchio_intestatario_gas', {
                          ...vecchioIntestatarioGas,
                          codice_fiscale: e.target.value.toUpperCase()
                        })}
                      />
                    </div>
                  </div>
                )}
                
                {/* Voltura Mortis Causa Gas: nascosto per i clienti business
                    (la voltura per successione e' una casistica domestica). */}
                {!isBusiness && (
                  <>
                    <div className={styles.checkboxContainer}>
                      <label className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          {...register('voltura_mortis_causa_gas')}
                        />
                        <span className={styles.checkboxCustom}>
                          <i className="bi bi-check-lg" />
                        </span>
                        <span className={styles.checkboxText}>
                          <strong>Voltura mortis causa su utenza domestica di residenza</strong>
                          <small>
                            Da selezionare se si e titolari di un diritto di successione relativo all'immobile oggetto della fornitura
                          </small>
                        </span>
                      </label>
                    </div>

                    {mortisCausaGas && (
                      <div className={styles.mortisCausaDetails}>
                        <div className={styles.mortisCausaHeader}>
                          <i className="bi bi-calendar-event" />
                          <span>Dati per Voltura Mortis Causa</span>
                        </div>

                        <div className={styles.formGrid}>
                          <Input
                            label="Data Decesso"
                            icon="calendar-event"
                            type="date"
                            required
                            error={errors.mortis_causa_data_decesso_gas?.message}
                            {...register('mortis_causa_data_decesso_gas', {
                              required: mortisCausaGas ? 'Data decesso obbligatoria per mortis causa' : false,
                              validate: (value) => {
                                if (!mortisCausaGas || !value) return true
                                const date = new Date(value)
                                const today = new Date()
                                today.setHours(0, 0, 0, 0)
                                if (date > today) return 'La data di decesso non può essere nel futuro'
                                return true
                              }
                            })}
                          />
                        </div>

                        {formData.stesso_indirizzo && (
                          <div className={styles.checkboxContainer} style={{ marginTop: '1rem' }}>
                            <label className={styles.checkboxLabel}>
                              <input
                                type="checkbox"
                                className={styles.checkbox}
                                {...register('mortis_causa_residenza_6_mesi_gas')}
                              />
                              <span className={styles.checkboxCustom}>
                                <i className="bi bi-check-lg" />
                              </span>
                              <span className={styles.checkboxText}>
                                <strong>Dichiarazione di residenza</strong>
                                <small>
                                  Dichiaro di risiedere nell'indirizzo di fornitura da almeno 6 mesi
                                </small>
                              </span>
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            
            <div className={styles.divider}>
              <span>Dati Contatore</span>
            </div>
            
            <div className={styles.formGrid}>
              <div className={styles.fullWidth}>
                <Input
                  label="PDR (Punto di Riconsegna)"
                  icon="upc"
                  required
                  maxLength={14}
                  placeholder="12345678901234"
                  hint="Trovi il PDR nella bolletta del gas (14 cifre)"
                  error={errors.pdr_gas?.message}
                  {...register('pdr_gas', {
                    required: hasGas ? 'PDR obbligatorio' : false,
                    pattern: {
                      value: /^[0-9]{14}$/,
                      message: 'PDR deve essere di 14 cifre'
                    }
                  })}
                />
              </div>
              
              <Input
                label="Matricola Contatore"
                icon="gear"
                uppercase
                placeholder="12345678"
                error={errors.matricola_contatore?.message}
                {...register('matricola_contatore')}
              />
              
              <Input
                label="Consumo Annuo (Smc)"
                icon="graph-up"
                type="number"
                placeholder="1000"
                required
                error={errors.consumo_annuo_gas?.message}
                {...register('consumo_annuo_gas', {
                  valueAsNumber: true,
                  required: hasGas ? 'Consumo annuo obbligatorio' : false,
                  min: hasGas ? { value: 500, message: 'Il consumo annuo Gas non puo essere inferiore a 500 Smc' } : undefined
                })}
              />
            </div>
            
            <div className={styles.divider}>
              <span>Dati Offerta</span>
            </div>
            
            <div className={styles.formGrid}>
              {/* Profilo calcolato automaticamente dal consumo annuo */}
              <div className={styles.profiloCalcolatoInline}>
                <label>Profilo <span className={styles.required}>*</span></label>
                {profiloGasValue ? (
                  <div className={styles.profiloBadgeInline}>
                    <i className="bi bi-fire" />
                    Profilo {famigliaGas} {profiloGasValue}
                    <span className={styles.profiloRangeSmall}>({getProfiloGasRange(profiloGasValue, isBusiness)})</span>
                  </div>
                ) : (
                  <div className={styles.profiloPlaceholderInline}>
                    <i className="bi bi-info-circle" />
                    Inserisci consumo annuo
                  </div>
                )}
                <input type="hidden" {...register('profilo_gas')} />
              </div>
              
              <Input
                label="Convenzione/Promo"
                icon="tag"
                placeholder="Codice convenzione"
                hint="Codice convenzione o promo (opzionale)"
                error={errors.convenzione_gas?.message}
                {...register('convenzione_gas')}
              />
            </div>
            
            {/* Categoria d'uso Gas */}
            <div className={styles.categoriaUsoSection}>
              <div className={styles.categoriaUsoHeader}>
                <i className="bi bi-list-check" />
                <span>Categoria d'uso</span>
              </div>
              <div className={styles.categoriaUsoGrid}>
                {CATEGORIE_USO_GAS.map((cat) => (
                  <label key={cat.value} className={styles.categoriaUsoItem}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={categoriaUsoGas.includes(cat.value)}
                      onChange={() => toggleCategoriaUsoGas(cat.value)}
                    />
                    <span className={styles.checkboxCustom}>
                      <i className="bi bi-check-lg" />
                    </span>
                    <span className={styles.categoriaUsoLabel}>{cat.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Richiesta esecuzione immediata:
            - DOMESTICO: solo voltura luce, modificabile dall'operatore.
            - BUSINESS: qualsiasi voltura/subentro (luce o gas), pre-flaggata
              e bloccata (vincolo contrattuale Hera per partita IVA). */}
        {(needsVolturaLuce || richiestaEsecuzioneImmediataLocked) && (
          <div className={styles.checkboxContainer}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                className={styles.checkbox}
                {...register('richiesta_esecuzione_immediata')}
                disabled={richiestaEsecuzioneImmediataLocked}
                onClick={(e) => {
                  if (richiestaEsecuzioneImmediataLocked) {
                    // Hard-lock: nessuna interazione possibile sul checkbox.
                    e.preventDefault()
                  }
                }}
              />
              <span className={styles.checkboxCustom}>
                <i className="bi bi-check-lg" />
              </span>
              <span className={styles.checkboxText}>
                <strong>Richiedo l'esecuzione immediata del servizio</strong>
                <small>
                  Dichiaro di voler usufruire del servizio durante il periodo di recesso di 14 giorni.
                  Sono consapevole che in caso di ripensamento dovrò sostenere i costi per le prestazioni già effettuate.
                  {richiestaEsecuzioneImmediataLocked && (
                    <>
                      {' '}
                      <em>Per le pratiche Business questa dichiarazione è obbligatoria e non removibile.</em>
                    </>
                  )}
                </small>
              </span>
            </label>
          </div>
        )}

        {/* ============================================
            TITOLARITA IMMOBILE (voltura e subentro)
            ============================================ */}
        {needsDichiarazione && (
          <>
        <div className={styles.divider}>
              <span>Titolarita Immobile</span>
        </div>

            <div className={styles.supplySection}>
              <div className={styles.supplySectionHeader}>
                <i className="bi bi-house-door-fill" />
                <span>Titolarita Immobile</span>
              </div>

              {/* Dropdown Titolarita */}
              <div className={styles.selectSection}>
                <label className={styles.selectLabel}>
                  <span>Tipo Titolarita <span className={styles.required}>*</span></span>
                </label>
                <select
                  className={styles.select}
                  {...register('titolarita_immobile', {
                    required: needsDichiarazione ? 'Seleziona il tipo di titolarita' : false
                  })}
                >
                  <option value="">-- Seleziona --</option>
                  {TITOLARITA_IMMOBILE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.titolarita_immobile && (
                  <span className={styles.errorMessage}>{errors.titolarita_immobile.message}</span>
                )}
              </div>

              {/* Dati Catastali - solo se PROPRIETARIO */}
              {watch('titolarita_immobile') === 'proprietario' && (
                <div className={styles.catastaliSection}>
                  <div className={styles.catastaliHeader}>
                    <i className="bi bi-geo-alt" />
                    <span>Dati Catastali</span>
                  </div>
                  <div className={styles.formGrid}>
                    <Input
                      label="Sezione"
                      icon="grid-3x3"
                      placeholder=""
                      {...register('catasto_sezione')}
                    />
                    <Input
                      label="Foglio"
                      icon="file-earmark"
                      placeholder=""
                      {...register('catasto_foglio')}
                    />
                    <Input
                      label="Particella"
                      icon="grid"
                      placeholder=""
                      {...register('catasto_particella')}
                    />
                    <Input
                      label="Subalterno"
                      icon="diagram-3"
                      placeholder=""
                      {...register('catasto_subalterno')}
                    />
                  </div>
                </div>
              )}

              {/* Note per verifica - solo se ALTRO o USUFRUTTUARIO */}
              {(watch('titolarita_immobile') === 'altro' || watch('titolarita_immobile') === 'usufruttuario') && (
                <div className={styles.noteSection}>
                  <label className={styles.textareaLabel}>
                    <span>Indicare dati utili a consentire la verifica</span>
                  </label>
                  <textarea
                    className={styles.textarea}
                    rows={3}
                    placeholder="Inserisci eventuali note o dati aggiuntivi..."
                    {...register('titolarita_note')}
                  />
                </div>
              )}

              {/* Dati Locazione - solo se LOCATARIO, LOCATARIO EDILIZIA PUBBLICA o COMODATARIO */}
              {(watch('titolarita_immobile') === 'locatario' || 
                watch('titolarita_immobile') === 'locatario_edilizia_pubblica' || 
                watch('titolarita_immobile') === 'comodatario') && (
                <div className={styles.catastaliSection}>
                  <div className={styles.catastaliHeader}>
                    <i className="bi bi-file-earmark-text" />
                    <span>Dati Contratto di Locazione</span>
                  </div>
                  <div className={styles.formGrid}>
                    <Input
                      label="Agenzia delle Entrate"
                      icon="building"
                      placeholder="Es. Torino 1"
                      required
                      error={errors.locazione_agenzia_entrate?.message}
                      {...register('locazione_agenzia_entrate', {
                        required: (watch('titolarita_immobile') === 'locatario' || 
                                   watch('titolarita_immobile') === 'locatario_edilizia_pubblica' || 
                                   watch('titolarita_immobile') === 'comodatario') 
                                   ? 'Campo obbligatorio' : false
                      })}
                    />
                    <Input
                      label="Data Registrazione"
                      type="date"
                      icon="calendar3"
                      {...register('locazione_data_registrazione')}
                    />
                    <Input
                      label="Numero Locazione"
                      icon="hash"
                      placeholder=""
                      {...register('locazione_numero')}
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ============================================
            SELEZIONE PRODOTTO
            ============================================ */}
        <div className={styles.divider}>
          <span>Selezione Prodotto</span>
        </div>

        {formData.company ? (
          <p className={styles.autoProdottoNote}>
            Prodotto luce/gas proposto in base al comune di fornitura (
            {formData.company === 'estenergy' ? 'Estenergy' : 'Heracomm'}
            ). Puoi modificarlo se necessario.
          </p>
        ) : null}

        <div className={styles.prodottiSection}>
          {/* Prodotto Luce */}
          {hasLuce && (
            <div className={styles.prodottoCard}>
              <div className={styles.prodottoHeader}>
                <i className="bi bi-lightbulb-fill" />
                <span>PRODOTTO LUCE <span className={styles.required}>*</span></span>
              </div>
              
              <div className={styles.selectSection}>
                <select
                  className={styles.select}
                  {...register('prodotto_luce_id', { setValueAs: (v) => v ? parseInt(v, 10) : null })}
                >
                  <option value="">--</option>
                  {prodottiLuce.map((p) => (
                    <option key={p.id} value={p.id}>{p.nome}</option>
                  ))}
                </select>
                <span className={styles.selectHint}>PRODOTTO (precompilato se possibile)</span>
              </div>

              {prodottoLuceId && (
                <div className={styles.cteLink} style={{ marginTop: '0.5rem' }}>
                  <a
                    href={`/backend/api/v1/condizioni-tecniche/${prodottoLuceId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.cteLinkAnchor}
                  >
                    <i className="bi bi-file-pdf" /> Scarica CTE - Condizioni Tecniche Economiche
                  </a>
                </div>
              )}

              {/* VAS "Hera NoProblem" Luce - solo operatori abilitati (Adventure) */}
              {vasEnabled && (
                <div className={styles.checkboxContainer} style={{ marginTop: '0.75rem' }}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      {...register('vas_luce')}
                    />
                    <span className={styles.checkboxCustom}>
                      <i className="bi bi-check-lg" />
                    </span>
                    <span className={styles.checkboxText}>
                      <strong>Aggiungi servizio Hera NoProblem Luce</strong>
                      <small>Il plico verra' compilato e firmato dal cliente in coda al contratto</small>
                    </span>
                  </label>
                </div>
              )}

              <div className={styles.profiloCalcolato}>
                <div className={styles.profiloCalcolatoHeader}>
                  <i className="bi bi-calculator" />
                  <span>Profilo <span className={styles.required}>*</span></span>
                </div>
                {profiloEE ? (
                  <div className={styles.profiloCalcolatoContent}>
                    <span className={styles.profiloBadge}>
                      <i className="bi bi-lightning-charge" />
                      Profilo {famigliaLuce} {profiloEE}
            </span>
                    <span className={styles.profiloRange}>{getProfiloLuceRange(profiloEE, isBusiness)}</span>
                  </div>
                ) : (
                  <div className={styles.profiloPlaceholder}>
                    <i className="bi bi-info-circle" />
                    <span>Inserisci il consumo annuo per calcolare il profilo</span>
                  </div>
                )}
                <input type="hidden" {...register('profilo_ee')} />
              </div>
            </div>
          )}

          {/* Prodotto Gas */}
          {hasGas && (
            <div className={styles.prodottoCard}>
              <div className={styles.prodottoHeader}>
                <i className="bi bi-fire" />
                <span>PRODOTTO GAS <span className={styles.required}>*</span></span>
              </div>
              
              <div className={styles.selectSection}>
                <select
                  className={styles.select}
                  {...register('prodotto_gas_id', { setValueAs: (v) => v ? parseInt(v, 10) : null })}
                >
                  <option value="">--</option>
                  {prodottiGas.map((p) => (
                    <option key={p.id} value={p.id}>{p.nome}</option>
                  ))}
                </select>
                <span className={styles.selectHint}>PRODOTTO (precompilato se possibile)</span>
              </div>

              {prodottoGasId && (
                <div className={styles.cteLink} style={{ marginTop: '0.5rem' }}>
                  <a
                    href={`/backend/api/v1/condizioni-tecniche/${prodottoGasId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.cteLinkAnchor}
                  >
                    <i className="bi bi-file-pdf" /> Scarica CTE - Condizioni Tecniche Economiche
                  </a>
                </div>
              )}

              {/* VAS "Hera NoProblem" Gas - solo operatori abilitati (Adventure) */}
              {vasEnabled && (
                <div className={styles.checkboxContainer} style={{ marginTop: '0.75rem' }}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      {...register('vas_gas')}
                    />
                    <span className={styles.checkboxCustom}>
                      <i className="bi bi-check-lg" />
                    </span>
                    <span className={styles.checkboxText}>
                      <strong>Aggiungi servizio Hera NoProblem Gas</strong>
                      <small>Il plico verra' compilato e firmato dal cliente in coda al contratto</small>
                    </span>
                  </label>
                </div>
              )}

              <div className={styles.profiloCalcolato}>
                <div className={styles.profiloCalcolatoHeader}>
                  <i className="bi bi-calculator" />
                  <span>Profilo <span className={styles.required}>*</span></span>
                </div>
                {profiloGasValue ? (
                  <div className={styles.profiloCalcolatoContent}>
                    <span className={styles.profiloBadge}>
                      <i className="bi bi-fire" />
                      Profilo {famigliaGas} {profiloGasValue}
            </span>
                    <span className={styles.profiloRange}>{getProfiloGasRange(profiloGasValue, isBusiness)}</span>
                  </div>
                ) : (
                  <div className={styles.profiloPlaceholder}>
                    <i className="bi bi-info-circle" />
                    <span>Inserisci il consumo annuo per calcolare il profilo</span>
                  </div>
                )}
                <input type="hidden" {...register('profilo_gas')} />
              </div>
            </div>
          )}
        </div>

        {/* ============================================
            SCHEDA TECNICA (solo business)
            ============================================ */}
        {isBusiness && (
          <>
            <div className={styles.divider}>
              <span>Scheda Tecnica di Fornitura</span>
            </div>

            <div className={styles.supplySection}>
              <div className={styles.supplySectionHeader}>
                <i className="bi bi-building-gear" />
                <span>Attivita Svolta</span>
              </div>

              <div className={styles.formGrid}>
                <div className={styles.fullWidth}>
                  <label className={styles.selectLabel}>Tipo Attivita *</label>
                  <select
                    className={styles.select}
                    {...register('tipo_attivita', { required: 'Tipo attivita obbligatorio' })}
                  >
                    <option value="">Seleziona attivita...</option>
                    {ATTIVITA_SVOLTE.map(a => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </select>
                  {errors.tipo_attivita && (
                    <span className={styles.errorMessage}>{errors.tipo_attivita.message}</span>
                  )}
                </div>
                <Input
                  label="Codice ATECO"
                  icon="upc-scan"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="471140"
                  hint="Solo cifre, senza punti (es. 471140)"
                  error={errors.codice_ateco?.message}
                  {...register('codice_ateco', {
                    pattern: {
                      value: /^[0-9]{6}$/,
                      message: 'Inserisci 6 cifre, senza punti (es. 471140)'
                    },
                    onChange: (e) => {
                      // Strip live di punti, spazi e qualsiasi non-cifra cosi'
                      // il filler PDF (6 caselle) riceve sempre 6 cifre nette.
                      const v = e.target.value as string
                      const cleaned = v.replace(/[^0-9]/g, '').slice(0, 6)
                      if (cleaned !== v) e.target.value = cleaned
                    }
                  })}
                />
                {/* sopra c'e' un <div> per il select tipo attivita',
                    il grid lo gestisce automaticamente */}
              </div>
            </div>

            <div className={styles.supplySection}>
              <div className={styles.supplySectionHeader}>
                <i className="bi bi-receipt" />
                <span>Sezione Imposte</span>
              </div>

              <div className={styles.formGrid}>
                {hasLuce && (
                  <div>
                    <label className={styles.selectLabel}>Aliquota IVA Energia Elettrica</label>
                    <select
                      className={styles.select}
                      {...register('iva_aliquota_ee')}
                    >
                      <option value="">Standard</option>
                      <option value="22">22%</option>
                      <option value="10">10% (agevolata)</option>
                      <option value="esenzione">Esenzione</option>
                    </select>
                  </div>
                )}
                {hasGas && (
                  <div>
                    <label className={styles.selectLabel}>Aliquota IVA Gas Naturale</label>
                    <select
                      className={styles.select}
                      {...register('iva_aliquota_gas')}
                    >
                      <option value="">Standard</option>
                      <option value="22">22%</option>
                      <option value="10">10% (agevolata)</option>
                      <option value="esenzione">Esenzione</option>
                    </select>
                  </div>
                )}
              </div>

              {hasGas && (
                <div className={styles.checkboxContainer}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      {...register('accisa_usi_industriali')}
                    />
                    <span className={styles.checkboxCustom}>
                      <i className="bi bi-check" />
                    </span>
                    <span className={styles.checkboxText}>
                      <strong>Accisa per usi industriali</strong>
                      <small>Richiesta applicazione aliquota accisa gas per combustione per usi industriali (D.Lgs n. 504/95 art. 26 TUA)</small>
                    </span>
                  </label>
                </div>
              )}
            </div>

            {hasLuce && (
              <div className={styles.supplySection}>
                <div className={styles.supplySectionHeader}>
                  <i className="bi bi-lightning-charge" />
                  <span>Dettagli Tecnici Energia Elettrica</span>
                </div>
                <div className={styles.formGrid}>
                  <div>
                    <label className={styles.selectLabel}>Tensione di Fornitura</label>
                    <div className={styles.radioGroup}>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          value="bt"
                          {...register('tensione_fornitura')}
                        />
                        <span>BT (Bassa Tensione)</span>
                      </label>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          value="mt"
                          {...register('tensione_fornitura')}
                        />
                        <span>MT (Media Tensione)</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className={styles.selectLabel}>Provenienza</label>
                    <div className={styles.radioGroup}>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          value="libero"
                          {...register('provenienza_mercato')}
                        />
                        <span>Mercato libero</span>
                      </label>
                      <label className={styles.radioLabel}>
                        <input
                          type="radio"
                          value="regolamentato"
                          {...register('provenienza_mercato')}
                        />
                        <span>Mercato regolamentato</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ============================================
            DIARIO DEI CONSUMI (sempre visibile)
            ============================================ */}
        <div className={styles.divider}>
          <span>Diario dei Consumi</span>
        </div>

        <div className={styles.checkboxContainer}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              className={styles.checkbox}
              {...register('diario_consumi_attivo')}
            />
            <span className={styles.checkboxCustom}>
              <i className="bi bi-check-lg" />
            </span>
            <span className={styles.checkboxText}>
              <strong>Attiva Diario dei Consumi</strong>
              <small>Monitora e ottimizza i tuoi consumi energetici</small>
            </span>
          </label>
        </div>

        {diarioConsumiAttivo && (
          <div className={styles.supplySection}>
            <div className={styles.supplySectionHeader}>
              <i className="bi bi-house-door" />
              <span>Informazioni Abitazione</span>
            </div>
            
            <div className={styles.formGrid}>
              <Input
                label="Superficie (mq)"
                icon="rulers"
                type="number"
                placeholder="es. 80"
                hint="Superficie dell'abitazione in metri quadrati"
                error={errors.superficie_abitazione?.message}
                {...register('superficie_abitazione', {
                  valueAsNumber: true
                })}
              />
              
              {isBusiness ? (
                <Input
                  label="Numero Addetti"
                  icon="people"
                  type="number"
                  placeholder="es. 10"
                  hint="Numero di addetti nello stabile"
                  error={errors.numero_addetti?.message}
                  {...register('numero_addetti', {
                    valueAsNumber: true
                  })}
                />
              ) : (
                <Input
                  label="Numero Abitanti"
                  icon="people"
                  type="number"
                  placeholder="es. 3"
                  hint="Numero di persone residenti"
                  error={errors.numero_abitanti?.message}
                  {...register('numero_abitanti', {
                    valueAsNumber: true
                  })}
                />
              )}
            </div>
            
          {/* Tipologia Abitazione e Proprietario (solo domestico) */}
          {!isBusiness && (
          <div className={styles.diarioGrid}>
            {/* Tipologia Abitazione */}
            <div className={styles.diarioColumn}>
              <div className={styles.diarioColumnHeader}>
                <i className="bi bi-building" />
                <span>Tipologia Abitazione</span>
              </div>
              <div className={styles.diarioOptions}>
                <button
                  type="button"
                  className={clsx(
                    styles.diarioOptionButton,
                    watch('tipologia_abitazione') === 'appartamento' && styles.diarioOptionActive
                  )}
                  onClick={() => setValue('tipologia_abitazione', 'appartamento')}
                >
                    <i className="bi bi-building" />
                  <span>Appartamento</span>
                  {watch('tipologia_abitazione') === 'appartamento' && (
                      <i className="bi bi-check-lg" />
                  )}
                </button>
                
                <button
                  type="button"
                  className={clsx(
                    styles.diarioOptionButton,
                    watch('tipologia_abitazione') === 'casa_indipendente' && styles.diarioOptionActive
                  )}
                  onClick={() => setValue('tipologia_abitazione', 'casa_indipendente')}
                >
                    <i className="bi bi-house" />
                  <span>Casa Indipendente</span>
                  {watch('tipologia_abitazione') === 'casa_indipendente' && (
                      <i className="bi bi-check-lg" />
                  )}
                </button>
              </div>
            </div>
            
            {/* Proprietario - sempre attivo */}
            <div className={styles.diarioColumn}>
              <div className={styles.diarioColumnHeader}>
                <i className="bi bi-key" />
                <span>Proprietario/Affittuario</span>
              </div>
              <div className={styles.diarioOptions}>
                <div className={clsx(styles.diarioOptionButton, styles.diarioOptionActive)}>
                  <i className="bi bi-person-check" />
                  <span>Proprietario</span>
                  <i className="bi bi-check-lg" />
                </div>
              </div>
            </div>
          </div>
          )}
        </div>
        )}

        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            icon="arrow-left"
            onClick={prevStep}
          >
            Indietro
          </Button>
          
          <Button
            type="submit"
            icon="arrow-right"
            iconPosition="right"
          >
            Continua
          </Button>
        </div>
      </form>

      {/* Modal Costi Voltura: mostra la sezione corrispondente al tipo di
          fornitura per cui l'operatore sta attivando la voltura (luce/gas) */}
      <CostiVolturaModal
        isOpen={showCostiVolturaModal}
        onClose={handleVolturaClose}
        onAccept={handleVolturaAccepted}
        showLuce={pendingVolturaType === 'luce'}
        showGas={pendingVolturaType === 'gas'}
      />
    </>
  )
}
